import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Button, Icon, IconButton } from "../../components/ui";

import "./send-invoice-modal.scss";

/**
 * Confirmation modal for the INVOICE step's SEND INVOICE button. Loads a preview
 * (the client name + email the invoice will be sent to, and the store whose
 * branding is used — resolved server-side from the order's store_id_m, i.e. the
 * same store that fills the email's logo/name/from variables) so the rep can
 * confirm before anything goes out. Nothing is emailed until Send is clicked.
 * On success it calls onSent() so the flow can advance to Commissions.
 */
const SendInvoiceModal = ({ orderId, fileId, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `/api/order-management/${orderId}/invoice-email-preview`,
        );
        if (alive) setPreview(data);
      } catch {
        if (alive) setError("Couldn’t load the invoice recipient details.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderId]);

  const send = async () => {
    if (!preview?.has_email) return;
    setSending(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `/api/order-management/${orderId}/send-invoice-email`,
        { fileId },
      );
      onSent?.(data?.sent_at);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Couldn’t send the invoice email. Please try again.",
      );
      setSending(false);
    }
  };

  return createPortal(
    <div className="sim__overlay" onMouseDown={onClose}>
      <div
        className="sim"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sim__header">
          <h3>Send Invoice</h3>
          <IconButton label="Close" variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size="sm" />
          </IconButton>
        </div>
        <div className="sim__body">
          {loading ? (
            <p className="sim__hint">Loading…</p>
          ) : (
            <>
              <p className="sim__hint">The invoice will be emailed to the client:</p>
              <dl className="sim__list">
                <div>
                  <dt>Name</dt>
                  <dd>{(preview?.first_name || "").trim() || "—"}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{preview?.email || "— none on file —"}</dd>
                </div>
                <div>
                  <dt>Sending as</dt>
                  <dd>{preview?.store_name || "—"}</dd>
                </div>
              </dl>
              {!preview?.has_email && (
                <div className="sim__error">
                  This client has no email address on file, so the invoice can’t be
                  emailed.
                </div>
              )}
            </>
          )}
          {error && <div className="sim__error">{error}</div>}
        </div>
        <div className="sim__footer">
          <Button
            variant="secondary"
            data-btn="invoice-cancel-send"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-btn="invoice-confirm-send"
            onClick={send}
            disabled={loading || !preview?.has_email}
            loading={sending}
          >
            Send invoice
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendInvoiceModal;
